import { Timestamp } from "firebase/firestore";

/**
 * Algoritmos de planejamento inteligente de aulas — sem dependência de Firebase.
 *
 * LÓGICA POR OBJETIVOS (não por tópicos):
 * - Cada atividade tem N objetivos, cada objetivo tem M tópicos
 * - A média de um objetivo = média das notas dos seus tópicos (de todos os alunos da turma)
 * - O ciclo semanal distribui 2 OBJETIVOS por semana
 * - Objetivos com tópicos fundamentais têm prioridade
 * - Objetivos com média mais baixa aparecem mais vezes no ciclo
 */

const getISOWeekNumber = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const getWeekKey = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const year = d.getUTCFullYear();
    const week = getISOWeekNumber(dateStr);
    return `${year}-W${String(week).padStart(2, "0")}`;
};

export { getWeekKey };

/**
 * Calcula a média da turma POR OBJETIVO.
 */
export const calculateObjectiveAverages = (evaluations, objectives, levels) => {
    const levelOrderMap = new Map();
    levels.forEach(l => levelOrderMap.set(String(l.id), Number(l.order || 0)));

    const objData = {};
    objectives.forEach(obj => {
        const topicEntries = {};
        (obj.topics || []).forEach(t => {
            topicEntries[t.id] = {
                id: t.id,
                name: t.name,
                isFundamental: !!t.isFundamental,
                scores: []
            };
        });

        objData[obj.id] = {
            id: obj.id,
            name: obj.name,
            topicCount: (obj.topics || []).length,
            hasFundamental: (obj.topics || []).some(t => !!t.isFundamental),
            topics: topicEntries
        };
    });

    evaluations.forEach(evalDoc => {
        if (!evalDoc?.objectives) return;
        evalDoc.objectives.forEach(evalObj => {
            const objId = String(evalObj.id || evalObj.idObjective);
            if (!objData[objId]) return;

            (evalObj.topics || []).forEach(t => {
                const topicId = String(t.id || t.idTopic);
                if (!objData[objId].topics[topicId]) return;
                const levelValue = levelOrderMap.get(String(t.idLevel)) ?? 0;
                objData[objId].topics[topicId].scores.push(levelValue);
            });
        });
    });

    const result = {};
    Object.values(objData).forEach(obj => {
        const topicResults = [];
        let totalTopicAvg = 0;
        let topicsWithScores = 0;

        Object.values(obj.topics).forEach(topic => {
            const avg = topic.scores.length > 0
                ? topic.scores.reduce((a, b) => a + b, 0) / topic.scores.length
                : 0;
            topicResults.push({
                id: topic.id,
                name: topic.name,
                isFundamental: topic.isFundamental,
                avg: Math.round(avg * 100) / 100,
                totalMembers: topic.scores.length
            });
            totalTopicAvg += avg;
            topicsWithScores++;
        });

        // Fundamentais primeiro; em caso de empate, menor média (maior defasagem) primeiro
        topicResults.sort((a, b) => {
            if (a.isFundamental && !b.isFundamental) return -1;
            if (!a.isFundamental && b.isFundamental) return 1;
            return a.avg - b.avg;
        });

        const objectiveAvg = topicsWithScores > 0
            ? totalTopicAvg / topicsWithScores
            : 0;

        result[obj.id] = {
            id: obj.id,
            name: obj.name,
            avg: Math.round(objectiveAvg * 100) / 100,
            topicCount: obj.topicCount,
            hasFundamental: obj.hasFundamental,
            topics: topicResults
        };
    });

    return result;
};

/**
 * Gera o ciclo semanal de OBJETIVOS (2 por semana), repetindo inteligentemente
 * os de maior defasagem e espaçando-os ao longo do ciclo.
 */
export const generateWeeklyCycle = (objectiveAverages, maxLevelValue, masteryThreshold, objectivesPerWeek = 2) => {
    const objectives = Object.values(objectiveAverages);
    if (objectives.length === 0) return [];

    let pendingObjectives = objectives.filter(o => o.avg < masteryThreshold || o.hasFundamental);

    if (pendingObjectives.length === 0) {
        pendingObjectives = objectives;
    }

    if (pendingObjectives.length <= objectivesPerWeek) {
        return [pendingObjectives.map(o => ({
            id: o.id,
            name: o.name,
            hasFundamental: o.hasFundamental,
            avgScore: o.avg,
            topicCount: o.topicCount,
            topics: o.topics,
            reason: o.hasFundamental ? "fundamental" : "regular"
        }))];
    }

    const processedObjectives = pendingObjectives.map(o => {
        const baseDeficit = Math.max(0.1, maxLevelValue - o.avg);
        const deficitRatio = baseDeficit / (maxLevelValue || 1);

        // >= 60% de defasagem = 3 repetições; >= 30% = 2; < 30% = 1
        let multiplicity = 1;
        if (deficitRatio >= 0.6) {
            multiplicity = 3;
        } else if (deficitRatio >= 0.3) {
            multiplicity = 2;
        }

        // Bônus para fundamentais (+1 repetição, teto 3)
        if (o.hasFundamental) {
            multiplicity = Math.min(3, multiplicity + 1);
        }

        const effectiveWeight = baseDeficit * (o.hasFundamental ? 1.5 : 1.0);

        return { ...o, multiplicity, effectiveWeight, remainingReps: multiplicity };
    });

    processedObjectives.sort((a, b) => b.effectiveWeight - a.effectiveWeight);

    const totalSlots = processedObjectives.reduce((sum, o) => sum + o.multiplicity, 0);
    const numWeeks = Math.ceil(totalSlots / objectivesPerWeek);

    processedObjectives.forEach(o => {
        o.multiplicity = Math.min(o.multiplicity, numWeeks);
        o.remainingReps = o.multiplicity;
    });

    const cycleWeeks = Array.from({ length: numWeeks }, () => []);

    for (let w = 0; w < numWeeks; w++) {
        while (cycleWeeks[w].length < objectivesPerWeek) {
            let chosenObj = null;

            // Restrição estrita: sem repetição na semana atual nem na anterior
            for (const obj of processedObjectives) {
                if (obj.remainingReps <= 0) continue;
                if (cycleWeeks[w].some(o => o.id === obj.id)) continue;
                if (w > 0 && cycleWeeks[w - 1].some(o => o.id === obj.id)) continue;
                chosenObj = obj;
                break;
            }

            // Fallback relaxado: apenas garante sem duplicata na mesma semana
            if (!chosenObj) {
                for (const obj of processedObjectives) {
                    if (obj.remainingReps <= 0) continue;
                    if (cycleWeeks[w].some(o => o.id === obj.id)) continue;
                    chosenObj = obj;
                    break;
                }
            }

            if (!chosenObj) break;

            cycleWeeks[w].push({
                id: chosenObj.id,
                name: chosenObj.name,
                hasFundamental: chosenObj.hasFundamental,
                avgScore: chosenObj.avg,
                topicCount: chosenObj.topicCount,
                topics: chosenObj.topics,
                reason: chosenObj.hasFundamental
                    ? "fundamental"
                    : (chosenObj.effectiveWeight > maxLevelValue * 0.5 ? "priority" : "regular")
            });

            chosenObj.remainingReps--;
        }
    }

    // Garantir que todas as semanas tenham objectivesPerWeek objetivos
    for (let w = 0; w < numWeeks; w++) {
        while (cycleWeeks[w].length < objectivesPerWeek) {
            const fillObj = processedObjectives.find(obj => !cycleWeeks[w].some(o => o.id === obj.id));
            if (!fillObj) break;

            cycleWeeks[w].push({
                id: fillObj.id,
                name: fillObj.name,
                hasFundamental: fillObj.hasFundamental,
                avgScore: fillObj.avg,
                topicCount: fillObj.topicCount,
                topics: fillObj.topics,
                reason: fillObj.hasFundamental
                    ? "fundamental"
                    : (fillObj.effectiveWeight > maxLevelValue * 0.5 ? "priority" : "regular")
            });
        }
    }

    return cycleWeeks;
};

/**
 * Distribui o ciclo pelas sessões futuras agrupadas por semana ISO.
 */
export const distributePlanToSessions = (sessions, cycle, evaluationEventId, memberCount, classAvgOverall) => {
    if (!cycle.length || !sessions.length) return sessions;

    const weekMap = {};
    sessions.forEach(s => {
        const weekKey = getWeekKey(s.sessionDate);
        if (!weekMap[weekKey]) weekMap[weekKey] = [];
        weekMap[weekKey].push(s);
    });

    const sortedWeeks = Object.keys(weekMap).sort();

    const result = [];
    sortedWeeks.forEach((weekKey, weekIndex) => {
        const cyclePosition = weekIndex % cycle.length;
        const weekObjectives = cycle[cyclePosition];

        weekMap[weekKey].forEach(session => {
            result.push({
                ...session,
                lessonPlan: {
                    generatedAt: Timestamp.now(),
                    evaluationEventId: evaluationEventId || "",
                    weekKey,
                    cyclePosition: cyclePosition + 1,
                    cycleLength: cycle.length,
                    objectivesPerWeek: weekObjectives.length,
                    objectives: weekObjectives,
                    classMemberCount: memberCount,
                    classAvgOverall: Math.round(classAvgOverall * 100) / 100
                }
            });
        });
    });

    return result;
};

/**
 * Verifica se uma sessão precisa de regeneração do plano.
 */
export const needsRegeneration = (session, currentEventId) => {
    if (!session.lessonPlan) return true;
    if (!currentEventId) return false;
    return session.lessonPlan.evaluationEventId !== currentEventId;
};
